// 由于项目中没有安装 Vuex，我们可以使用本地存储来实现简单的状态管理
import { defineStore } from 'pinia'
import api from '@/api'
import { markHomeLoginPopupPending } from '@/utils/homeLoginPopup'

// 添加常量配置
const STORAGE_KEY = {
  TOKEN: 'token',
  USER_INFO: 'userInfo'
}

// 定义用户存储
export const useUserStore = defineStore('user', {
  state: () => {
    // 安全地解析 JSON，避免解析 null 或 undefined
    let userInfo = {}
    try {
      const storedInfo = localStorage.getItem('userInfo')
      if (storedInfo) {
        userInfo = JSON.parse(storedInfo)
      }
    } catch (error) {
      console.error('解析用户信息失败:', error)
      // 解析失败时使用空对象
      localStorage.removeItem('userInfo')
    }
    
    return {
      token: localStorage.getItem('token') || '',
      userInfo: userInfo
    }
  },
  
  getters: {
    isLoggedIn: (state) => !!state.token,
    nickname: (state) => state.userInfo?.nickName || '游客',
    isVisitor: (state) => state.userInfo?.isVisitor || false,
    avatarUrl: (state) => state.userInfo?.avatarUrl || ''
  },
  
  actions: {
    // 设置token
    setToken(token) {
      this.token = token
      if (token) {
        localStorage.setItem('token', token)
      } else {
        localStorage.removeItem('token')
      }
    },
    
    // 设置用户信息
    setUserInfo(userInfo) {
      this.userInfo = userInfo
      if (userInfo) {
        localStorage.setItem('userInfo', JSON.stringify(userInfo))
      } else {
        localStorage.removeItem('userInfo')
      }
    },
    
    // 获取用户信息
    async getUserInfo() {
      try {
        const res = await api.getUserInfo()
        if (res.code === 200) {
          this.setUserInfo(res.data)
          return res.data
        }
        throw new Error(res.msg || '获取用户信息失败')
      } catch (error) {
        console.error('获取用户信息失败:', error)
        throw error
      }
    },
    
    // 登录
    login(token, userInfo) {
      this.setToken(token)
      this.setUserInfo(userInfo)
      markHomeLoginPopupPending()
    },
    
    // 更新用户详细信息
    async updateDetailInfo(userInfo) {
      try {
        const res = await api.updateDetailInfo(userInfo)
        if (res.code === 200) {
          const updatedInfo = {
            ...this.userInfo,
            ...res.data,
            isInfoComplete: true
          }
          this.setUserInfo(updatedInfo)
          return res
        }
        throw new Error(res.msg || '更新用户信息失败')
      } catch (error) {
        console.error('更新用户信息失败:', error)
        throw error
      }
    },

    // 清除所有存储
    clearStorage() {
      this.token = ''
      this.userInfo = {}
      localStorage.removeItem('token')
      localStorage.removeItem('userInfo')
    },

    // 退出登录
    logout() {
      this.clearStorage()
    },
    
    // 更新用户信息
    updateUserInfo(userInfo) {
      this.setUserInfo({
        ...this.userInfo,
        ...userInfo
      })
    },

    // 重置 token
    resetToken() {
      this.token = ''
      localStorage.removeItem('token')
    }
  }
})

// 为了兼容之前的代码，提供一个类似的接口
const UserManager = {
  getToken() {
    return useUserStore().token
  },
  
  setToken(token) {
    useUserStore().setToken(token)
  },
  
  getUserInfo() {
    return useUserStore().userInfo
  },
  
  setUserInfo(userInfo) {
    useUserStore().setUserInfo(userInfo)
  },
  
  login(token, userInfo) {
    useUserStore().login(token, userInfo)
  },
  
  logout() {
    useUserStore().logout()
  },
  
  isLoggedIn() {
    return useUserStore().isLoggedIn
  }
}

export default UserManager 